import { Link } from 'react-router-dom';
import { ReactNode } from 'react';
import SEO from './SEO';
import PublicHeader from './PublicHeader';

interface LegalPageLayoutProps {
  title: string;
  description: string;
  children: ReactNode;
}

// =============================================================================
// DESIGN TOKENS (matching LandingPage)
// =============================================================================
const tokens = {
  container: 'max-w-[1140px] mx-auto px-4 sm:px-6 lg:px-8',
  heading: {
    h1: 'text-3xl sm:text-4xl font-bold text-white leading-tight',
    h2: 'text-xl sm:text-2xl font-semibold text-white mt-10 mb-4',
  },
  text: {
    base: 'text-slate-300 leading-relaxed',
    small: 'text-sm text-slate-500',
  },
  accent: 'text-primary-400',
};

export default function LegalPageLayout({ title, description, children }: LegalPageLayoutProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <SEO title={title} description={description} />
      
      <PublicHeader />

      {/* Main Content */}
      <main className={`${tokens.container} py-12 md:py-16`}>
        <article className="max-w-3xl mx-auto prose-slate">
          {children}
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-12">
        <div className={tokens.container}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/features/audit-timeline" className="text-slate-400 hover:text-white transition-colors">Audit Timeline</Link></li>
                <li><Link to="/features/drift-detection" className="text-slate-400 hover:text-white transition-colors">Drift Detection</Link></li>
                <li><Link to="/features/exception-tracking" className="text-slate-400 hover:text-white transition-colors">Exception Tracking</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Use Cases</h4>
              <ul className="space-y-2">
                <li><Link to="/github-change-control" className="text-slate-400 hover:text-white transition-colors">GitHub Change Control</Link></li>
                <li><Link to="/soc2-change-management-evidence" className="text-slate-400 hover:text-white transition-colors">SOC 2 Evidence</Link></li>
                <li><Link to="/outsourced-software-change-control" className="text-slate-400 hover:text-white transition-colors">Outsourced Development</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/read-only-github-audit" className="text-slate-400 hover:text-white transition-colors">Read-Only Audit</Link></li>
                <li><Link to="/sample-timeline" className="text-slate-400 hover:text-white transition-colors">Sample Timeline</Link></li>
                <li><Link to="/faq" className="text-slate-400 hover:text-white transition-colors">FAQ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/security" className="text-slate-400 hover:text-white transition-colors">Security</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
              <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
                <p>&copy; {new Date().getFullYear()} EvidentTrail. All rights reserved.</p>
                <span className="hidden md:inline">·</span>
                <p>Operated by EvidentTrail (Early Access), Netherlands.</p>
              </div>
              <div className="flex items-center gap-4">
                <Link 
                  to="/#contact" 
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
